/**
 * Typed Platform API surface and the TanStack Query hooks over it.
 *
 * The types here describe what the Platform API actually returns, which is deliberately narrow:
 * identifiers are the public prefixed form, an API key's plaintext appears in exactly one response
 * shape, and nothing carries a session token or a key digest. If a field is absent from these
 * types it is because it must never reach the browser.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiRequest } from './api-client';
import { clearSessionHint } from './session-hint';

export interface UserProfile {
  id: string;
  email: string;
  displayName: string | null;
  emailVerified: boolean;
}

export interface SessionResponse {
  user: UserProfile;
  workspace: { id: string; name: string; planCode: string };
  session: { id: string; expiresAt: string };
}

/** A provider the Platform API can sign in with. Only configured ones are ever listed. */
export type SignInProvider = 'google';

/** A provider account connected to this user. The provider's own account ID is never sent. */
export interface ConnectedIdentity {
  id: string;
  provider: SignInProvider;
  /** The provider's address for the account, which may differ from the account's own. */
  email: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface SignInMethods {
  hasPassword: boolean;
  identities: ConnectedIdentity[];
}

export interface SessionSummary {
  id: string;
  current: boolean;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  userAgent: string | null;
  ipAddress: string | null;
}

export interface PlanLimits {
  monthlyCredits: number;
  requestsPerSecond: number;
  concurrentRenders: number;
  maxTimeoutMs: number;
  maxDelayMs: number;
  maxCacheTtl: number;
}

export interface WorkspaceDetail {
  id: string;
  name: string;
  slug: string;
  planCode: string;
  planName: string;
  entitlementVersion: number;
  billingPeriod: { id: string; startsAt: string; endsAt: string };
  limits: PlanLimits;
  membership: { role: string; joinedAt: string };
  owner: { id: string; email: string; displayName: string | null };
  createdAt: string;
}

/** Safe key metadata. Note the absence of any field that could carry the secret. */
export interface ApiKeySummary {
  id: string;
  name: string;
  keyPrefix: string;
  keySuffix: string;
  status: string;
  /** Fixed at creation: a publishable key is refused as a bearer token, and cannot be changed. */
  kind: 'secret' | 'publishable';
  /**
   * The key itself, for a publishable one; null for a secret key.
   *
   * Publishable keys are meant to be seen -- this one is in the `src` of every image that embeds it
   * -- so it is shown in the list rather than hidden behind the show-once dialog. The null on a
   * secret key is not an omission: its value exists nowhere to show.
   */
  key: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/**
 * The creation response, and the only place `apiKey` exists.
 *
 * It is held in component state for as long as the confirmation dialog is open and then dropped.
 * It is never written to browser storage, never put in a query cache, and never logged.
 */
export type CreatedApiKey =
  | (ApiKeySummary & {
      kind: 'secret';
      /** The key, shown once at creation. */
      apiKey: string;
    })
  | (ApiKeySummary & {
      kind: 'publishable';
      /**
       * The same string as `key`, which is also kept and listed. Read this one at creation and
       * `key` afterwards, and no caller has to branch on the kind for the key itself.
       */
      apiKey: string;
      /**
       * The secret that signs query strings for this key, and the only value here shown once: it
       * is derived rather than stored, so the API cannot display it again.
       *
       * A secret key has none. It is refused in a signed URL, so a signing secret for it would
       * sign nothing.
       */
      signingSecret: string;
    });

export const queryKeys = {
  session: ['session'] as const,
  sessions: ['sessions'] as const,
  authProviders: ['auth', 'providers'] as const,
  signInMethods: ['account', 'identities'] as const,
  workspace: ['workspace', 'current'] as const,
  apiKeys: ['api-keys'] as const,
  usageSummary: (window: UsageWindow) => ['usage', 'summary', window] as const,
  // Filters are part of the key so changing one starts a new query rather than showing the
  // previous filter's rows while the new ones load.
  requestLogs: (filters: RequestLogFilters) => ['request-logs', filters] as const,
  requestLog: (requestId: string) => ['request-logs', 'detail', requestId] as const,
};

/** Reporting window. Absent bounds mean "the API's default", which is the last 30 days. */
export interface UsageWindow extends Record<string, string | undefined> {
  from?: string;
  to?: string;
}

export interface RequestLogFilters extends Record<string, string | number | undefined> {
  status?: string;
  cacheStatus?: string;
  keyId?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

export interface RequestLogSummary {
  requestId: string;
  status: 'succeeded' | 'failed' | 'rejected' | 'throttled';
  errorCode: string | null;
  httpStatus: number;
  targetHostname: string | null;
  cacheStatus: 'HIT' | 'MISS' | 'BYPASS';
  credits: number;
  durationMs: number;
  keyId: string | null;
  requestedAt: string;
}

export interface RequestLogDetail extends RequestLogSummary {
  /** Null for a request logged before the mode was stored. */
  authenticationMode: 'api_key' | 'signed_url' | 'demo' | null;
  cacheSource: string;
  creditDisposition: string;
  rendererVersion: string | null;
  rendererFunctionVersion: string | null;
  rendererCold: boolean | null;
  browserStarted: boolean | null;
  targetStatus: number | null;
  redirectCount: number | null;
  timings: { totalMs: number | null; navigationMs: number | null; screenshotMs: number | null };
  options: Record<string, unknown> | null;
}

export interface RequestLogPage {
  requests: RequestLogSummary[];
  nextCursor: string | null;
}

export interface UsageSummary {
  window: { from: string; to: string };
  totals: {
    requests: number;
    succeeded: number;
    failed: number;
    rejected: number;
    throttled: number;
    cacheHits: number;
    credits: number;
    averageDurationMs: number;
  };
  billingPeriod: { id: string; startsAt: string; endsAt: string };
  monthlyCredits: number;
  creditsUsedThisPeriod: number;
  hourly: { hourStart: string; requests: number; credits: number; cacheHits: number }[];
}

/** Build a query string, omitting empty values so an unset filter is absent rather than blank. */
function queryString(params: Readonly<Record<string, string | number | undefined>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded === '' ? '' : `?${encoded}`;
}

/**
 * Resolve the current session.
 *
 * `retry: false` because a `401` here is the normal logged-out answer, not a failure worth
 * retrying, and `null` rather than an error keeps route guards branching on a value.
 */
export function useSession(): UseQueryResult<SessionResponse | null> {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: async () => {
      try {
        return await apiRequest<SessionResponse>('/session');
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
}

export function useWorkspace(enabled = true): UseQueryResult<WorkspaceDetail> {
  return useQuery({
    queryKey: queryKeys.workspace,
    queryFn: () => apiRequest<WorkspaceDetail>('/workspaces/current'),
    enabled,
  });
}

export function useApiKeys(enabled = true): UseQueryResult<{ keys: ApiKeySummary[] }> {
  return useQuery({
    queryKey: queryKeys.apiKeys,
    queryFn: () => apiRequest<{ keys: ApiKeySummary[] }>('/api-keys'),
    enabled,
  });
}

/**
 * Usage for a window, and the billing period's credits alongside it.
 *
 * The previous window's answer stays on screen while a new one loads. Changing the Overview's time
 * range would otherwise blank the page for a moment -- including the billing-period figures, which
 * do not depend on the range at all.
 */
export function useUsageSummary(window: UsageWindow = {}): UseQueryResult<UsageSummary> {
  return useQuery({
    queryKey: queryKeys.usageSummary(window),
    queryFn: () => apiRequest<UsageSummary>(`/usage/summary${queryString(window)}`),
    placeholderData: (previous) => previous,
  });
}

/**
 * One page of the request log.
 *
 * `placeholderData` keeps the previous page visible while the next one loads, so paging does not
 * blank the table and shift the layout under the reader's cursor.
 */
export function useRequestLogs(filters: RequestLogFilters = {}): UseQueryResult<RequestLogPage> {
  return useQuery({
    queryKey: queryKeys.requestLogs(filters),
    queryFn: () => apiRequest<RequestLogPage>(`/request-logs${queryString(filters)}`),
    placeholderData: (previous) => previous,
  });
}

/**
 * One request.
 *
 * `retry: false` because the expected failure is a 404 -- a request that does not exist, or belongs
 * to another workspace -- and retrying it three times only delays the error the reader needs to see.
 */
export function useRequestLog(requestId: string): UseQueryResult<RequestLogDetail> {
  return useQuery({
    queryKey: queryKeys.requestLog(requestId),
    queryFn: () => apiRequest<RequestLogDetail>(`/request-logs/${encodeURIComponent(requestId)}`),
    retry: false,
  });
}

export function useSessions(enabled = true): UseQueryResult<{ sessions: SessionSummary[] }> {
  return useQuery({
    queryKey: queryKeys.sessions,
    queryFn: () => apiRequest<{ sessions: SessionSummary[] }>('/sessions'),
    enabled,
  });
}

/**
 * The providers this environment can sign in with.
 *
 * An empty list on failure rather than an error: the sign-in form works without them, and a
 * provider button that leads to "unavailable" is worse than no button.
 */
export function useAuthProviders(): UseQueryResult<SignInProvider[]> {
  return useQuery({
    queryKey: queryKeys.authProviders,
    queryFn: async () => {
      try {
        const body = await apiRequest<{ providers?: SignInProvider[] }>('/auth/providers');
        return body.providers ?? [];
      } catch {
        return [];
      }
    },
    retry: false,
    // Configuration, not data: it changes with a deploy.
    staleTime: Infinity,
  });
}

export function useSignInMethods(enabled = true): UseQueryResult<SignInMethods> {
  return useQuery({
    queryKey: queryKeys.signInMethods,
    queryFn: () => apiRequest<SignInMethods>('/account/identities'),
    enabled,
  });
}

export function useDisconnectIdentity(): UseMutationResult<
  { status: string },
  Error,
  { id: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ status: string }>(`/account/identities/${encodeURIComponent(input.id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.signInMethods }),
  });
}

export function useLogin(): UseMutationResult<
  { user: UserProfile },
  Error,
  { email: string; password: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ user: UserProfile }>('/auth/login', {
        method: 'POST',
        body: input,
      }),
    // A fresh session invalidates every cached answer, including anything cached while logged out.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useRegister(): UseMutationResult<
  { status: string },
  Error,
  { email: string; password: string; displayName?: string | undefined }
> {
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ status: string }>('/auth/register', {
        method: 'POST',
        body: input,
      }),
  });
}

export function useForgotPassword(): UseMutationResult<
  { status: string },
  Error,
  { email: string }
> {
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ status: string }>('/auth/forgot-password', {
        method: 'POST',
        body: input,
      }),
  });
}

export function useResetPassword(): UseMutationResult<
  { status: string },
  Error,
  { token: string; password: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ status: string }>('/auth/reset-password', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useLogout(): UseMutationResult<{ status: string }, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<{ status: string }>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      // Runs on failure too: the cookies are gone either way once the server has been told, and
      // leaving stale workspace data in the cache after a logout would be worse than a refetch.
      clearSessionHint();
      queryClient.clear();
    },
  });
}

/** Change the name the account is greeted by. `null` removes it. */
export function useUpdateProfile(): UseMutationResult<
  { user: UserProfile },
  Error,
  { displayName: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ user: UserProfile }>('/account/profile', { method: 'PATCH', body: input }),
    onSuccess: ({ user }) => {
      // The session payload carries the profile the Overview greets by, so it is updated in place
      // rather than refetched.
      queryClient.setQueryData<SessionResponse | null>(queryKeys.session, (current) =>
        current === null || current === undefined ? current : { ...current, user },
      );
    },
  });
}

export function useUpdateWorkspace(): UseMutationResult<WorkspaceDetail, Error, { name: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<WorkspaceDetail>('/workspaces/current', {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: (workspace) => {
      queryClient.setQueryData(queryKeys.workspace, workspace);
      // The workspace name also appears in the session payload's summary.
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useCreateApiKey(): UseMutationResult<
  CreatedApiKey,
  Error,
  { name: string; kind: 'secret' | 'publishable' }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => apiRequest<CreatedApiKey>('/api-keys', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys }),
  });
}

export function useRevokeApiKey(): UseMutationResult<{ status: string }, Error, { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ status: string }>(`/api-keys/${input.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys }),
  });
}

export function useRevokeSession(): UseMutationResult<{ status: string }, Error, { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ status: string }>(`/sessions/${input.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
  });
}

/**
 * Close the account. Irreversible, and it ends the current session.
 *
 * `onSettled` rather than `onSuccess`, matching `useLogoutAll`: the server clears the session
 * cookies on the way out and the user's status is no longer `active`, so the session is dead
 * whatever the client thinks. Clearing the cached session and the marketing-site hint on failure
 * too is the safe direction -- the worst case is a signed-in user being asked to sign in again.
 */
export function useDeleteAccount(): UseMutationResult<{ status: string }, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<{ status: string }>('/account', { method: 'DELETE' }),
    onSettled: () => {
      clearSessionHint();
      queryClient.clear();
    },
  });
}

export function useLogoutAll(): UseMutationResult<
  { status: string; revoked: number },
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ status: string; revoked: number }>('/sessions/logout-all', {
        method: 'POST',
      }),
    onSettled: () => {
      // Logout-all includes the caller's own session, so this ends the current session too.
      clearSessionHint();
      queryClient.clear();
    },
  });
}

// ---------------------------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------------------------

export type BillingInterval = 'month' | 'year';

/**
 * What the Billing page shows, as the Platform API reports it.
 *
 * Every field is read, never assumed: the plan changes only when Paddle's webhook reaches the API,
 * so after a checkout this is what the page polls until the new plan appears.
 */
export interface BillingOverview {
  billingEnabled: boolean;
  plan: { code: string; name: string };
  subscription: {
    status: 'active' | 'trialing' | 'past_due' | 'paused' | 'canceled';
    planCode: string;
    interval: BillingInterval;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    canceledAt: string | null;
  } | null;
  prices: {
    planCode: string;
    planName: string;
    interval: BillingInterval;
    amountCents: number;
    currency: string;
    monthlyCredits: number | null;
    purchasable: boolean;
  }[];
  /** Every public plan, Free included, with the limits it grants, in display order. */
  plans: {
    code: string;
    name: string;
    limits: {
      monthlyCredits: number;
      concurrentRenders: number;
      maxTimeoutMs: number;
      maxCacheTtl: number;
    };
  }[];
}

export const billingQueryKey = ['billing', 'plan'] as const;

/** How often to ask whether Paddle's webhook has landed, while waiting for it. */
const BILLING_POLL_MS = 2_000;

/**
 * What a billing request is waiting to move away from: the plan, and how it is billed.
 *
 * Both, because a switch from monthly to yearly on the same plan changes only the interval. Waiting
 * on the plan alone would call that switch confirmed before it happened -- or, as it did, never.
 */
export function billingStateOf(overview: BillingOverview): string {
  return `${overview.plan.code}:${overview.subscription?.interval ?? 'none'}`;
}

/**
 * The billing overview, polled while waiting for a plan or billing interval to change.
 *
 * Polling covers the few seconds between Paddle taking a payment and its webhook changing the
 * workspace. Pass `billingStateOf` the overview as it was when the request was made: polling runs
 * while that is still the state reported, and stops by itself the moment it is not.
 */
export function useBillingOverview(
  waitingToLeaveState: string | null = null,
): UseQueryResult<BillingOverview> {
  return useQuery({
    queryKey: billingQueryKey,
    queryFn: () => apiRequest<BillingOverview>('/billing/plan'),
    refetchInterval: (query) =>
      waitingToLeaveState !== null &&
      query.state.data !== undefined &&
      billingStateOf(query.state.data) === waitingToLeaveState
        ? BILLING_POLL_MS
        : false,
  });
}

export function useStartCheckout(): UseMutationResult<
  { transactionId: string },
  Error,
  { planCode: string; interval: BillingInterval }
> {
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ transactionId: string }>('/billing/checkout-transaction', {
        method: 'POST',
        body: input,
      }),
  });
}

export function useChangePlan(): UseMutationResult<
  { status: string },
  Error,
  { planCode: string; interval: BillingInterval }
> {
  return useMutation({
    mutationFn: (input) =>
      apiRequest<{ status: string }>('/billing/change-plan', { method: 'POST', body: input }),
  });
}

/**
 * What a plan change would charge or credit now, as Paddle calculates it.
 *
 * Amounts are in the currency's minor units (cents) and include tax.
 */
export interface PlanChangePreview {
  currency: string;
  chargeNow: number;
  credit: number;
  recurring: number | null;
  nextBilledAt: string | null;
  interval: BillingInterval;
}

/** Price a plan change before it is confirmed. Changes nothing. */
export function usePlanChangePreview(): UseMutationResult<
  PlanChangePreview,
  Error,
  { planCode: string; interval: BillingInterval }
> {
  return useMutation({
    mutationFn: (input) =>
      apiRequest<PlanChangePreview>('/billing/change-plan/preview', {
        method: 'POST',
        body: input,
      }),
  });
}

export function usePortalSession(): UseMutationResult<{ url: string }, Error, void> {
  return useMutation({
    mutationFn: () =>
      apiRequest<{ url: string }>('/billing/customer-portal-session', { method: 'POST' }),
  });
}
